import React from 'react';

import { useTranslation } from '@/i18n';
import { useProgressivityForm } from '@/hooks/attendance/configuration/useProgressivityForm';
import type { Progressivity } from '@/api/domains/attendance';

import { PermissionGuard, SidePanelFormLayout } from '@/components/layout';
import { PrimaryButton } from '@/components/ui/buttons/PrimaryButton';
import { LabelInput } from '@/components/ui/inputs/LabelInput';
import { DatePicker } from '@/components/ui/filters';


interface ProgressivityFormPanelProps {
  record?: Progressivity;
  onSuccess?: () => void;
}

export const ProgressivityFormPanel: React.FC<ProgressivityFormPanelProps> = (
  { record, onSuccess },
) => {
  const { t: translate } = useTranslation();
  const { form, isEditMode, isReady, onSubmit } = useProgressivityForm({ record, onSuccess });

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    errors,
    isSubmitted,
  } = form;

  const startDate = watch('startDate');

  return (
    <SidePanelFormLayout
      onSubmit={handleSubmit(onSubmit)}
      data-testid="progressivity-form-panel"
    >
      <SidePanelFormLayout.Fields>
        <DatePicker
          label={translate('attendance:configuration.workday.form.effectiveDate')}
          value={startDate}
          onChange={(date) => setValue('startDate', date)}
          required
          color={errors.startDate && isSubmitted ? 'error' : 'default'}
          supportingText={
            errors.startDate?.message && isSubmitted
              ? translate(errors.startDate.message)
              : undefined
          }
        />

        <LabelInput
          label={translate('attendance:configuration.workday.form.weeklyHours')}
          type="number"
          registration={register('weeklyHours')}
          required
          color={errors.weeklyHours && isSubmitted ? 'error' : 'default'}
          supportingText={
            errors.weeklyHours?.message && isSubmitted
              ? translate(errors.weeklyHours.message)
              : undefined
          }
        />
      </SidePanelFormLayout.Fields>

      <SidePanelFormLayout.Footer>
        <PermissionGuard action={isEditMode ? 'edit' : 'create'}>
          <PrimaryButton fullWidth type="submit" disabled={!isReady}>
            {isEditMode
              ? translate('attendance:configuration.workday.form.submitEdit')
              : translate('attendance:configuration.workday.form.submitCreate')}
          </PrimaryButton>
        </PermissionGuard>
      </SidePanelFormLayout.Footer>
    </SidePanelFormLayout>
  );
};

export default ProgressivityFormPanel;
